import React from 'react';
import useLogout from 'hooks/useLogout';
import { ISplitUrlLocation } from 'interfaces/routesInt';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom';
import urls from 'routes/urls';
import PharmacyOnboarding from 'components/shared/PharmacyOnboarding';
import { getDashboardPath } from 'utils/navigation';
import { useFetchUserData } from 'queries/auth/user';

interface IAuthMiddleware {
  isAuthProtected: boolean;
}

const AuthMiddleware = ({ isAuthProtected }: IAuthMiddleware) => {
  const storageKey = 'appOpenTime';
  const navigate = useNavigate();
  const { onHandleLogout } = useLogout();
  const { userData } = useFetchUserData();

  const { pathname } = useLocation();
  const splitLocation: ISplitUrlLocation = {
    segments: pathname.split('/'),
  };

  useEffect(() => {
    const initialTime = Date.now();
    localStorage.setItem(storageKey, initialTime.toString());

    const onStorageChange = (event: StorageEvent) => {
      if (
        event.key === storageKey &&
        event.oldValue !== initialTime.toString()
      ) {
        onHandleLogout();
      }
    };

    window.addEventListener('storage', onStorageChange);
    return () => window.removeEventListener('storage', onStorageChange);
  }, []);

  useEffect(() => {
    // Only check for tokens and redirect if the route is explicitly protected
    if (isAuthProtected) {
      const userToken =
        localStorage.getItem('refreshToken') ||
        localStorage.getItem('accessToken');
      
      if (!userToken) {
        navigate(urls.login);
      } else {
        // Correctly handle redirection from base /dashboard to specialized dashboards
        if (userData && (pathname === '/dashboard' || pathname === urls.dashboard)) {
          const dashboardPath = getDashboardPath(userData);
          if (pathname !== dashboardPath) {
            navigate(dashboardPath);
          }
        }
      }
    }
  }, [navigate, splitLocation, isAuthProtected, userData, pathname]);

  return isAuthProtected === true ? (
    <>
      <PharmacyOnboarding />
      <Outlet />
    </>
  ) : <Outlet />;
};

AuthMiddleware.prototype = {
  isAuthProtected: PropTypes.bool,
};

export default AuthMiddleware;
